import { useEffect, useMemo, useState } from 'react';
import {
  CATEGORY_SORTS, categoryListRows, nextCategorySort, pageCount as pagesFor,
} from '../domain/expenseForms';

// Sorting and paging for the categories table.
//
// The page is clamped: deleting enough categories to shrink the table would
// otherwise leave `page` past the end, the footer reading "3 of 1 pages" over
// an empty table. Anything that shrinks the row count pulls the page back.
//
// The Sort menu and the rows' order are `src/domain/expenseForms.js`'s.
export const SORTS = CATEGORY_SORTS;

export default function useCategoryList({ categoryRecords, categoryUsage }) {
  const [sort, setSort] = useState({ key: 'label', desc: false });
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(1);

  const rows = useMemo(
    () => categoryListRows(categoryRecords(), categoryUsage(), sort),
    [categoryRecords, categoryUsage, sort],
  );

  const pageCount = pagesFor(rows.length, perPage);

  useEffect(() => {
    setPage((p) => Math.min(p, pageCount));
  }, [pageCount]);

  // Clamped again on the way out: the effect above lands a render later, and
  // for that one render `page` can still be past the end.
  const safePage = Math.min(page, pageCount);
  const shown = rows.slice((safePage - 1) * perPage, safePage * perPage);

  // Clicking the column you are already sorted by reverses it, which is what a
  // sort menu that shows a direction arrow has to mean.
  const chooseSort = (key) =>
    setSort((cur) => nextCategorySort(cur, key));

  return {
    rows, shown, sort, chooseSort,
    page: safePage, pageCount, perPage,
    setPage,
    setPerPage: (n) => { setPerPage(n); setPage(1); },
  };
}
